import BlockedHeaderFieldList from '@Components/BlockedHeaderFieldList';
import {
  Flex,
  FlexProps,
  Input,
  InputProps,
  Switch,
  Text,
  Textarea,
  TextareaProps,
  TextLabel,
  TextLabelProps,
  TextProps,
  useColorMode,
  useColorStyle,
  useTheme,
} from '@tonic-one/react';
import { useMergeRefs } from '@tonic-one/react-hooks';
import React from 'react';
import { useTranslation } from 'react-i18next';
import EditableList from 'src/components/EditableList';
import HeaderFieldList from 'src/components/HeaderFieldList';
import { useWhyDidYouUpdate } from 'src/utils/hooks';

export const FormLabel = React.memo(
  ({
    required = false,
    withColon = true,
    align = 'left',
    label,
    ...rest
  }: {
    required?: boolean;
    label: string;
    withColon?: boolean;
    align?: 'left' | 'right' | 'center' | 'justify' | 'inherit';
  } & TextLabelProps) => {
    const [colorMode] = useColorMode();
    const [colorStyle] = useColorStyle({ colorMode });
    const { colors } = useTheme();
    const styles = {
      '--color': colors[colorStyle.color.error],
      textAlign: align,
    };
    return (
      <Flex height="32px" alignItems="center">
        <TextLabel
          className={required ? 'required' : ''}
          style={styles}
          fontSize="14px"
          lineHeight="20px"
          mr="6x"
          width="128px"
          {...rest}
        >
          {label === '' ? `${label}` : `${label}${withColon ? ':' : ''}`}
        </TextLabel>
      </Flex>
    );
  }
);
export const FormInput = React.forwardRef(
  ({ placeholder, ...rest }: InputProps, ref) => {
    const nodeRef = React.useRef();
    const combinedRef = useMergeRefs(nodeRef, ref);
    return (
      <Input
        ref={combinedRef}
        pt="6px"
        pb="6px"
        pl="12px"
        pr="8px"
        height="32px"
        placeholder={placeholder}
        width="416px"
        {...rest}
      />
    );
  }
);

export const FormTextarea = React.forwardRef(
  ({ placeholder, ...rest }: TextareaProps, ref) => {
    const nodeRef = React.useRef();
    const combinedRef = useMergeRefs(nodeRef, ref);
    return (
      <Textarea
        ref={combinedRef}
        pt="6px"
        pb="6px"
        pl="12px"
        pr="8px"
        height="78px"
        placeholder={placeholder}
        width="416px"
        {...rest}
      />
    );
  }
);

export const CheckboxTitle = React.memo(
  ({
    title,
    subTitle = '',
    ...rest
  }: {
    title: string;
    subTitle?: string;
  } & TextProps) => {
    const [colorMode] = useColorMode();
    const [colorStyle] = useColorStyle({ colorMode });
    return (
      <Flex direction="row">
        <Text
          fontSize="14px"
          lineHeight="20px"
          width="90px"
          color={colorStyle.color.primary}
          {...rest}
        >
          {title}
        </Text>
        {subTitle && (
          <Text
            color={colorStyle.color.secondary}
            fontSize="14px"
            lineHeight="20px"
          >
            {subTitle}
          </Text>
        )}
      </Flex>
    );
  }
);

export const TitleLabel = React.memo(
  ({ title, ...rest }: { title: string } & TextLabelProps) => {
    const [colorMode] = useColorMode();
    const [colorStyle] = useColorStyle({ colorMode });
    return (
      <TextLabel
        fontSize="18px"
        lineHeight="24px"
        color={colorStyle.color.primary}
        {...rest}
      >
        {title}
      </TextLabel>
    );
  }
);

export const FormList = React.memo(
  (props: {
    onDelete: (value: string) => void;
    onAdd: (value: string) => void;
    onExport: (entries: any) => void;
    onImport: () => void;
    list: {
      value: string;
      name: string;
    }[];
    limit: number;
    status: boolean;
    setStatus: (e: any) => void;
    label: string;
    size?: number;
    description?: string;
    width?: string;
    placeholder?: string;
    containerProps?: FlexProps;
  }) => {
    const { t } = useTranslation();
    const {
      onDelete,
      onAdd,
      onExport,
      onImport,
      list,
      limit,
      status,
      setStatus,
      label,
      description,
      width,
      size,
      placeholder,
      containerProps,
    } = props;
    useWhyDidYouUpdate('FormList', props);
    return (
      <Flex direction="column" ml="6x" mb="6x" {...containerProps}>
        <Flex alignItems="center" mb="3x">
          <FormLabel label={label} withColon={false} width="100%" />
          <Switch
            checked={status}
            value={Number(status)}
            onChange={setStatus}
          />
          <Text ml="3x" fontSize="14px" lineHeight="20px">
            {status ? t('common.label.on') : t('common.label.off')}
          </Text>
        </Flex>
        <EditableList
          onDelete={onDelete}
          onAdd={onAdd}
          onExport={onExport}
          onImport={onImport}
          list={list}
          disabled={!status}
          limit={limit}
          description={description || ''}
          width={width || '800px'}
          size={size || 4}
          placeholder={placeholder || ''}
        />
      </Flex>
    );
  }
);
interface Operator {
  value: string;
  name: string;
}

export const FormHeaderFieldList = React.memo(
  (props: {
    onDelete: (item: any) => void;
    onAdd: (item: any) => void;
    // onAddBlockedHeader: (item: RuleDefinitionsProps[]) => void;
    list: {
      key: string;
      value: string;
      operator: string;
    }[];
    limit: number;
    status: boolean;
    setStatus: (e: any) => void;
    label: string;
    operators: Operator[];
    headerMaxLength: number;
    description?: string;
    width?: string;
    size?: number;
    isBlockedHeaderFieldList?: boolean;
    [key: string]: any;
  }) => {
    const { t } = useTranslation();
    const {
      onDelete,
      onAdd,
      list,
      limit,
      status,
      setStatus,
      label,
      description,
      width,
      size,
      operators,
      headerMaxLength,
      isBlockedHeaderFieldList = false,
    } = props;
    useWhyDidYouUpdate('FormHeaderFieldList', props);
    return (
      <Flex direction="column" ml="25px" mb="24px">
        <Flex alignItems="center" mb="3x">
          <FormLabel label={label} withColon={false} width="100%" />
          <Switch checked={!!status} onChange={setStatus} />
          <Text ml="3x" fontSize="14px" lineHeight="20px">
            {status ? t('common.label.on') : t('common.label.off')}
          </Text>
        </Flex>
        {!isBlockedHeaderFieldList ? (
          <HeaderFieldList
            onDelete={onDelete}
            onAdd={onAdd}
            list={list}
            disabled={!status}
            limit={limit}
            description={description || ''}
            width={width || '800px'}
            size={size || 4}
            operators={operators}
            headerMaxLength={headerMaxLength}
          />
        ) : (
          <BlockedHeaderFieldList
            onDelete={onDelete}
            onAdd={onAdd}
            list={list}
            disabled={!status}
            limit={limit}
            description={description || ''}
            width={width || '800px'}
            size={size || 4}
            operators={operators}
            headerMaxLength={headerMaxLength}
          />
        )}
      </Flex>
    );
  }
);
